/* eslint-disable @atlassian/tangerine/import/no-parent-imports */

import React, { useCallback, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { Box, Grid } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { useAppContext } from '../../common/ui/app-context';
import { useBootstrap } from '../../controllers/store/bootstrap';
import { useSelectedFilterCount } from '../../controllers/store/search';

import {
	LiteAppFilterBar,
	LiteShowAllAppsButton,
	PromotedAppsProvider,
} from './filters/lite-app-filter-bar';
import {
	useElementWidth,
	useFiltersExpansionSync,
	useScrollState,
	useToggleClusterPosition,
} from './lite-header-hooks';

/**
 * Minimum width (in px) required for the filters section in combined row mode.
 * This accounts for the dropdown toggle button (~40px) plus at least one inline
 * filter (~200px) and some padding.
 */
const MIN_FILTERS_SECTION_WIDTH = 300;

/**
 * Estimated width (in px) per app pill in the app filter bar. Each pill is a
 * button containing an icon + label, typically 80–120px. We use a conservative
 * estimate to ensure we don't show the combined row when there isn't enough
 * space.
 */
const ESTIMATED_APP_PILL_WIDTH = 110;

/**
 * Fixed overhead in the app filter bar (in px): the "All" pill (~50px), the
 * "Show all apps" button (~120px when visible), gaps between pills (~12px each),
 * and the grid column gap between the app bar and filters section (16px).
 */
const APP_FILTER_BAR_FIXED_OVERHEAD = 200;

type LiteHeaderProps = {
	input: React.ReactNode;
	filters: React.ReactNode;
	/**
	 * Optional feedback button rendered in the header. Its position depends on
	 * the layout:
	 * - 2-row collapsed: to the right of the "More filters" toggle button.
	 * - 2-row expanded: to the right of the "Less filters" toggle button,
	 *   directly underneath the "Show all apps" button.
	 * - Combined row (limited apps): at the far right of the container.
	 * - Single app: at the far right of the container.
	 */
	feedback?: React.ReactNode;
};

const styles = cssMap({
	base: {
		position: 'sticky',
		top: '0',
		backgroundColor: token('elevation.surface'),
		width: '100%',
		backgroundClip: 'padding-box',
		zIndex: 200,
	},
	keyline: {
		borderBottom: `${token('border.width')} solid ${token('color.border')}`,
	},
	contentContainer: {
		margin: 'auto',
		maxWidth: '100%',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		alignItems: 'space-between',
		'@media (min-width: 48rem)': {
			maxWidth: '70rem',
		},
	},
	// 2-row grid: app filter bar on row 1, animated filters on row 2.
	// The toggle cluster is absolutely positioned outside the flow so it
	// can glide vertically between rows without disturbing the layout.
	gridTwoRow: {
		gridTemplateAreas: '"input" "appFilterBar" "filters"',
		gridTemplateColumns: '1fr',
		paddingTop: token('space.300'),
		paddingBottom: token('space.300'),
		// The toggle cluster is absolutely positioned within this grid, so
		// establish a positioning context.
		position: 'relative',
	},
	// Animates the filters row between collapsed (0fr) and expanded (1fr)
	// using grid-template-rows so we don't need a fixed height.
	// paddingTop is animated alongside grid-template-rows so the wrapper
	// contributes zero vertical space when collapsed (otherwise the bottom
	// of the header would have space.300 + space.150 instead of space.300).
	filtersAnimatedWrapper: {
		display: 'grid',
		gridTemplateRows: '0fr',
		transition:
			'grid-template-rows 200ms ease, opacity 200ms ease, transform 200ms ease, padding-top 200ms ease',
		opacity: 0,
		transform: 'translateY(-8px)',
		// Collapsed state contributes zero vertical space.
		paddingTop: token('space.0'),
	},
	filtersAnimatedWrapperExpanded: {
		gridTemplateRows: '1fr',
		opacity: 1,
		transform: 'translateY(0)',
		// Constant gap above the filters row when expanded.
		paddingTop: token('space.150'),
	},
	filtersAnimatedInner: {
		minHeight: '0px',
		overflow: 'hidden',
	},
	filtersRowContent: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
	},
	filtersRowFiltersArea: {
		flex: '1 1 0',
		minWidth: '0px',
	},
	filtersRowToggleArea: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		gap: token('space.150'),
		flex: '0 0 auto',
	},
	// Reserves the cluster's width at the right end of the filters row so
	// filter pills don't flow under the absolutely-positioned cluster.
	filtersRowToggleClusterSpacer: {
		flexShrink: 0,
		flexGrow: 0,
		overflow: 'hidden',
		transition: 'width 200ms ease',
	},
	// Suppress transition on initial paint to avoid animating from 0 → width.
	filtersRowToggleClusterSpacerNoTransition: {
		transition: 'none',
	},
	inputGridArea: {
		gridArea: 'input',
		display: 'flex',
		alignItems: 'center',
		// Provides the input → app filter bar gap (non-nav4 only).
		marginBottom: token('space.200'),
	},
	appFilterBarGridArea: {
		gridArea: 'appFilterBar',
		minWidth: '0px',
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
	},
	appFilterBarScroll: {
		minWidth: '0px',
		flex: '1 1 0',
		// Clip overflow so pills don't flow into the toggle cluster / "Show all apps".
		overflow: 'hidden',
	},
	// Absolutely-positioned cluster (More/Less filters + Feedback).
	// `top` is set inline and animates between the appFilterBar row center
	// (collapsed) and the filters row center (expanded).
	// `translateY(-50%)` so `top` targets the cluster's center Y.
	toggleCluster: {
		position: 'absolute',
		right: '0',
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
		transform: 'translateY(-50%)',
		transition: 'top 200ms ease',
		zIndex: 1,
	},
	// Suppress `top` transition on initial paint to avoid sliding from y=0.
	toggleClusterNoTransition: {
		transition: 'none',
	},
	// Hidden until first measurement commits — prevents a one-frame flash at y=0.
	// `visibility: hidden` (not `display: none`) preserves measurable layout.
	toggleClusterHidden: {
		visibility: 'hidden',
	},
	// Reserves the cluster's width in the appFilterBar row so "Show all apps"
	// doesn't overlap the cluster when collapsed. Faster than the cluster's
	// top transition (120ms vs 200ms) so "Show all apps" clears the way first.
	toggleClusterSpacer: {
		flexShrink: 0,
		flexGrow: 0,
		overflow: 'hidden',
		transition: 'width 120ms ease-out',
	},
	// Suppress width transition on initial paint.
	toggleClusterSpacerNoTransition: {
		transition: 'none',
	},
	filtersGridArea: {
		gridArea: 'filters',
	},
	gridSingleApp: {
		gridTemplateAreas: '"input . ." "filters . feedback"',
		gridTemplateColumns: '1fr auto auto',
		paddingTop: token('space.300'),
		paddingBottom: token('space.300'),
	},
	gridCombinedRow: {
		gridTemplateAreas: '"input . . ." "appFilterBar filtersDivider filters feedback"',
		gridTemplateColumns: 'auto auto 1fr auto',
		columnGap: token('space.150'),
		paddingTop: token('space.300'),
		paddingBottom: token('space.300'),
	},
	feedbackGridArea: {
		gridArea: 'feedback',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
	},
	paddingTop: {
		paddingTop: token('space.300'),
	},
	filtersDivider: {
		gridArea: 'filtersDivider',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderRightStyle: 'solid',
		alignSelf: 'stretch',
		marginBlock: token('space.100'),
	},
});

export const LiteHeader = ({ input, filters, feedback }: LiteHeaderProps): React.JSX.Element => {
	const { isNav4Enabled } = useAppContext();
	const { formatMessage } = useIntl();
	// `isFiltersExpanded` reflects the user-controlled / scroll-derived expansion
	// state of the "More filters" section. It starts expanded so the user lands
	// on a fully-revealed filter row, then auto-collapses when the page is
	// scrolled down and re-expands when the page is scrolled back to the top
	// (unless the user has any filter selected, in which case it stays expanded
	// regardless of scroll position).
	const [isFiltersExpanded, setIsFiltersExpanded] = useState(true);
	const [{ products }] = useBootstrap();
	const [{ selectedFilterCount }] = useSelectedFilterCount();
	const hasSelectedFilters = selectedFilterCount > 0;

	const isSingleApp = products.length <= 1;

	// Track the content container width. The container is always in the DOM
	// regardless of layout mode, so we get reliable width updates even when
	// the filters element is unmounted (2-row collapsed). Drives the
	// combined-row vs 2-row layout decision below.
	const {
		width: containerWidth,
		setNode: containerRefCallback,
		nodeRef: containerRef,
	} = useElementWidth();

	// Refs to the two rows whose `offsetTop` anchors the toggle cluster's
	// vertical position. State (not refs) so the `useToggleClusterPosition`
	// layout effect can re-run when they mount/unmount. The filters row's
	// element is also mirrored into a ref so the always-mounted scroll
	// handler in `useScrollState` can read its current `offsetHeight`
	// without re-binding every time the row's identity changes.
	const [appFilterBarRowEl, setAppFilterBarRowEl] = useState<HTMLDivElement | null>(null);
	const [filtersRowEl, _setFiltersRowEl] = useState<HTMLDivElement | null>(null);
	const filtersRowRef = useRef<HTMLDivElement | null>(null);
	const setFiltersRowEl = useCallback((node: HTMLDivElement | null) => {
		filtersRowRef.current = node;
		_setFiltersRowEl(node);
	}, []);

	// Resolve the page's actual scroll source and track:
	//  - isScrolled                    → drives the bottom-border keyline.
	//  - hasInsufficientScrollOverflow → guards against the auto-collapse
	//                                    feedback loop on landing /
	//                                    no-results / very short pages.
	const { isScrolled, hasInsufficientScrollOverflow } = useScrollState({
		containerRef,
		filtersRowRef,
	});

	// Drive `isFiltersExpanded` from the scroll signals (with the "filters
	// selected → always expanded" override). Manual button toggles bypass
	// this and call `setIsFiltersExpanded` directly.
	useFiltersExpansionSync({
		isScrolled,
		hasInsufficientScrollOverflow,
		hasSelectedFilters,
		setIsFiltersExpanded,
	});

	// Compute the absolutely-positioned toggle cluster's `top` between its
	// top-row (collapsed) and bottom-row (expanded) anchors, plus measure
	// its width (drives the row spacers) and report when both are first
	// committed (drives the initial-paint visibility/transition guards).
	const {
		toggleClusterTop,
		toggleClusterWidth,
		hasMeasuredInitialPosition,
		areTransitionsEnabled,
		setToggleClusterEl,
	} = useToggleClusterPosition({
		appFilterBarRowEl,
		filtersRowEl,
		isFiltersExpanded,
		containerWidth,
	});

	// Estimate the app filter bar's natural width based on the number of
	// connected products. We can't reliably measure the bar's intrinsic width
	// because in the 2-row grid layout it stretches to fill 1fr, and
	// scrollWidth reports the stretched size. Using a per-pill estimate avoids
	// this measurement problem while adapting to the number of connected apps.
	const estimatedAppBarWidth =
		products.length * ESTIMATED_APP_PILL_WIDTH + APP_FILTER_BAR_FIXED_OVERHEAD;

	// Determine whether there's enough remaining space in the container for
	// the filters section alongside the app filter bar.
	// When containerWidth is 0 (not yet measured), default to combined row
	// so the layout is immediately correct on wide viewports.
	const remainingWidth = containerWidth - estimatedAppBarWidth;
	const hasSufficientWidth = containerWidth === 0 || remainingWidth >= MIN_FILTERS_SECTION_WIDTH;
	const isCombinedRow = !isSingleApp && hasSufficientWidth;

	return (
		<>
			<Box
				xcss={cx(styles.base, isScrolled && !hasInsufficientScrollOverflow && styles.keyline)}
				testId="search-page-header"
			>
				<Box xcss={styles.contentContainer} ref={containerRefCallback}>
					{isSingleApp ? (
						// rowGap=0 in nav4 mode — the grid has 2 template rows but only
						// one is rendered, so a non-zero gap would appear above the filters.
						<Grid
							xcss={styles.gridSingleApp}
							columnGap="space.200"
							rowGap={isNav4Enabled ? 'space.0' : 'space.200'}
						>
							{!isNav4Enabled && <Box xcss={styles.inputGridArea}>{input}</Box>}
							<Box xcss={styles.filtersGridArea}>{filters}</Box>
							{feedback && <Box xcss={styles.feedbackGridArea}>{feedback}</Box>}
						</Grid>
					) : isCombinedRow ? (
						// rowGap=0 in nav4 mode — same reason as gridSingleApp above.
						<Grid
							xcss={styles.gridCombinedRow}
							rowGap={isNav4Enabled ? 'space.0' : 'space.200'}
							testId="lite-header-combined-row"
						>
							{!isNav4Enabled && <Box xcss={styles.inputGridArea}>{input}</Box>}
							<PromotedAppsProvider>
								<Box xcss={styles.appFilterBarGridArea}>
									<Box xcss={styles.appFilterBarScroll}>
										<LiteAppFilterBar />
									</Box>
									<LiteShowAllAppsButton />
								</Box>
							</PromotedAppsProvider>
							<Box xcss={styles.filtersDivider} />
							<Box xcss={styles.filtersGridArea}>{filters}</Box>
							{feedback && <Box xcss={styles.feedbackGridArea}>{feedback}</Box>}
						</Grid>
					) : (
						<Grid xcss={styles.gridTwoRow} rowGap="space.0" columnGap="space.0">
							{!isNav4Enabled && <Box xcss={styles.inputGridArea}>{input}</Box>}
							<PromotedAppsProvider>
								<Box xcss={styles.appFilterBarGridArea} ref={setAppFilterBarRowEl}>
									<Box xcss={styles.appFilterBarScroll}>
										<LiteAppFilterBar />
									</Box>
									<LiteShowAllAppsButton />
									{/* Spacer that pushes "Show all apps" left when collapsed,
									    reserving room for the absolutely-positioned cluster. */}
									{!isFiltersExpanded && (
										<Box
											xcss={cx(
												styles.toggleClusterSpacer,
												!areTransitionsEnabled && styles.toggleClusterSpacerNoTransition,
											)}
											style={{ width: `${toggleClusterWidth}px` }}
											aria-hidden
										/>
									)}
								</Box>
								<Box
									xcss={cx(
										styles.filtersGridArea,
										styles.filtersAnimatedWrapper,
										isFiltersExpanded && styles.filtersAnimatedWrapperExpanded,
									)}
									ref={setFiltersRowEl}
									// `inert` hides collapsed filters from tab order + a11y tree.
									{...(!isFiltersExpanded && {
										inert: '',
										'aria-hidden': true,
									})}
								>
									<Box xcss={styles.filtersAnimatedInner}>
										<Box xcss={styles.filtersRowContent}>
											<Box xcss={styles.filtersRowFiltersArea}>{filters}</Box>
											{/* Spacer that reserves room for the cluster at the right
											    of the filters row so pills don't flow underneath it. */}
											<Box
												xcss={cx(
													styles.filtersRowToggleClusterSpacer,
													!areTransitionsEnabled &&
														styles.filtersRowToggleClusterSpacerNoTransition,
												)}
												style={{
													width: isFiltersExpanded ? `${toggleClusterWidth}px` : '0px',
												}}
												aria-hidden
											/>
										</Box>
									</Box>
								</Box>
								{/* Toggle cluster — animates `top` between the two row anchors. */}
								<Box
									xcss={cx(
										styles.toggleCluster,
										!areTransitionsEnabled && styles.toggleClusterNoTransition,
										!hasMeasuredInitialPosition && styles.toggleClusterHidden,
									)}
									ref={setToggleClusterEl}
									style={{ top: `${toggleClusterTop}px` }}
								>
									<Button
										appearance="default"
										iconBefore={isFiltersExpanded ? ChevronUpIcon : ChevronDownIcon}
										onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
									>
										{isFiltersExpanded
											? formatMessage({
													id: 'search_page.header.less_filters',
													defaultMessage: 'Less filters',
													description:
														'Button to collapse the filters section in the search page header',
												})
											: formatMessage({
													id: 'search_page.header.more_filters',
													defaultMessage: 'More filters',
													description:
														'Button to expand the filters section in the search page header',
												})}
									</Button>
									{feedback}
								</Box>
							</PromotedAppsProvider>
						</Grid>
					)}
				</Box>
			</Box>
			{isNav4Enabled && <VisuallyHidden>{input}</VisuallyHidden>}
		</>
	);
};
